import { EventType } from "@/analytics/analytics-events";
import { ImportScansProperties } from "@/analytics/import-scans-properties";
import { useDataManagementFileHandler } from "@/hooks/use-data-management-file-handler";
import { useAppSelector } from "@/store/store-hooks";
import {
  selectCanUploadFocusFiles,
  selectCanUploadOrbisFiles,
} from "@/store/subscription-features/subscription-features-selectors";
import { selectCurrentUser } from "@/store/user-selectors";
import { DraftViewOpenFrom } from "@/utils/redirects";
import {
  appStateSelector,
  DataManagementPicker,
} from "@faro-lotv/data-management";
import {
  FaroText,
  FolderSmallIcon,
  grey,
  NO_TRANSLATE_CLASS,
  ScanIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import { Box, Stack } from "@mui/material";
import type { JSX } from "react";
import { ImportCard } from "./import-card";

interface Props {
  /** Identifier for the calling UI page/element provided to navigateToDataManagement. */
  from: DraftViewOpenFrom;
  /** Optional callback for closing the surrounding import data dialog. */
  onClose?(): void;
}

/**
 * @returns Card for importing scans via the Data Management editor.
 * Used in:
 *  - Import data dialog opened with the "Import" button.
 *  - Start screen shown for an empty project and a user with write access.
 */
export function ImportScansCard({ from, onClose }: Props): JSX.Element | null {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const projectId = useAppSelector(selectProjectId);
  const canImportOrbisFiles = useAppSelector(selectCanUploadOrbisFiles);
  const canImportFocusFiles = useAppSelector(selectCanUploadFocusFiles);
  const { isDevModeEnabled } = useAppSelector(appStateSelector);
  const canImportRoviFiles = isDevModeEnabled;

  const importScansExtensions =
    ".gls" +
    `${canImportOrbisFiles ? ", .geoslam" : ""}` +
    `${canImportFocusFiles ? ", .fls" : ""}` +
    `${canImportRoviFiles ? ", .rovi" : ""}`;

  const {
    shouldOpenFileExplorer,
    setShouldOpenFileExplorer,
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
  } = useDataManagementFileHandler({ projectId, from, onClose });

  if (!currentUser || !companyId || !projectId) {
    return null;
  }

  return (
    <>
      <DataManagementPicker
        type="all"
        currentUser={currentUser}
        companyId={companyId}
        projectId={projectId}
        shouldOpenFileExplorer={shouldOpenFileExplorer}
        setShouldOpenFileExplorer={setShouldOpenFileExplorer}
        isChecking={isCheckingFiles}
        setIsChecking={setIsCheckingFiles}
        onFinished={onFinishedCheckingFiles}
      />

      <ImportCard
        ariaLabel="import scans"
        icon={<ScanIcon sx={{ width: "46px", height: "46px" }} />}
        title="Scans"
        description={
          <Box>
            Upload a project folder with scans you’ve exported from your FARO
            laser scanner. The data will be processed, registered and published,
            ready for visualization and analysis.
            <br />
            Focus processing is in open beta.
          </Box>
        }
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText
              aria-label="scans extensions"
              variant="bodyS"
              className={NO_TRANSLATE_CLASS}
            >
              {importScansExtensions}
            </FaroText>
            <FolderSmallIcon sx={{ color: grey[600] }} />
          </Stack>
        }
        onClick={() => {
          Analytics.track<ImportScansProperties>(EventType.importScans, {
            via: from === "draftImportScansDialog" ? "draft view" : "live view",
          });
          setShouldOpenFileExplorer(true);
        }}
      />
    </>
  );
}
